import React from 'react';
import classnames from 'classnames';
import { map, noop, isEmpty, split, find, get } from 'lodash';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';


// local dependencies
import { Icon } from 'modules/icon-bank';
import { getLabel } from 'utils/getLabel';
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { ForceScroller } from 'modules/force-scroller';
import { getMemberIconColor } from 'resolvers/hipaaAndCoverage';
import { animator, AnimationManager } from 'core/modules/animation';
import { ANIMATION_NAMES } from 'constants/animation';
import { messageBus } from 'core/modules/message-bus';

const indicatorView = React.forwardRef( ( props, forwardedRef ) => {

    return(
        <div className='ui-member-details-header-family-unit__indicator' ref={forwardedRef}>
            <Icon name={ props.indicatorLogo } />
        </div>
    );
} );

// set display name
indicatorView.displayName = 'BrandIndicator';

const IndicatorView = animator( indicatorView, ANIMATION_NAMES.FADE_IN_SLIDE_Y, { distance: 30 } );

/*******************************/

// success view
export const memberDetailsHeaderFamilyUnitSuccess = ( props ) => {

    const { memberId, members } = props;
    const indicatorLogo = get( find( members, { id: memberId } ), 'indicatorLogo', '' );

    React.useEffect( () => {

        messageBus.trigger( MESSAGE_BUS_EVENTS.RELOAD_FORCE_SCROLLER );
      
      }, [ indicatorLogo ] );

    // widget class name
    const widgetClassName = classnames( 'ui-member-details-header-family-unit', props.className );

    // family unit class name
    const familyUnitClassName = classnames( 'ui-member-details-header-family-unit__family-unit', {
        'ui-member-details-header-family-unit__family-unit--medD': ! isEmpty( indicatorLogo )
    } );

    return (
        <div className={ widgetClassName }>
            {/* indicator logo */}
            {
                ! isEmpty( indicatorLogo ) && (

                    <AnimationManager>
                        <IndicatorView
                            indicatorLogo={ indicatorLogo }
                        />
                    </AnimationManager>
                )
            }

            {/* switch member functionality */}
            {
                isEmpty( props.callState ) ? null : (
                    <div className='ui-member-details-header-family-unit__switch-member' onClick={ () => props.onSwitchMemberClick() }>
                        <div className='ui-member-details-header-family-unit__switch-member__icon'>
                            <Icon name='switchMember' />
                        </div>
                        <div className='ui-member-details-header-family-unit__switch-member__title'>
                            { getLabel( props.switchUserAction.title )  }
                        </div>
                    </div>
                )
            }

            {/* family Unit */}
            <div className={ familyUnitClassName }>
                <ForceScroller
                    messageBus= { messageBus }
                >
                    {
                        map( props.members, ( member ) => {

                            // get icon color
                            const iconColor = getMemberIconColor( {
                                isActive: props.memberId === member.id,
                                coverageStatus: member.coverageStatus,
                            } );

                            // dynamic item class name
                            const itemClassName = classnames( 'ui-member-details-header-family-unit__family-unit__member', {
                                'ui-member-details-header-family-unit__family-unit__member--active': ( props.memberId === member.id )
                            } );
                            
                            return (
                                <div key={ member.id } className={ itemClassName } onClick={ () =>  props.onFamilyMemberClick( member ) }>
                                    <div className='ui-member-details-header-family-unit__family-unit__member__icon'>
                                        <Icon
                                            name={ member.gender }
                                            fill={ iconColor }
                                            stroke={ iconColor }
                                        />
                                    </div>

                                    <div className='ui-member-details-header-family-unit__family-unit__member__name'>
                                        {
                                            map( split( member.name, ' ' ), namePart => {
                                                return (
                                                    <div key={ namePart } className='ui-member-details-header-family-unit__family-unit__member__name__part'>
                                                        { namePart }
                                                    </div>
                                                );
                                            } )
                                        }
                                    </div>
                                </div>
                            );
                        } )
                    }
                </ForceScroller>
            </div>
        </div>
    );
};

// set display name
memberDetailsHeaderFamilyUnitSuccess.displayName = 'memberDetailsHeaderFamilyUnitSuccess';

// set default props
memberDetailsHeaderFamilyUnitSuccess.defaultProps = {
    switchUserAction: {},
    members: [],
    onSwitchMemberClick: noop,
    onFamilyMemberClick: noop,
};

// prop types
memberDetailsHeaderFamilyUnitSuccess.propTypes = {};

/*******************************/

// preloader view
export const memberDetailsHeaderFamilyUnitPreloader = () => {
    return (
        <div className='ui-member-details-header-family-unit-preloader'>
            <div className='ui-member-details-header-family-unit-preloader__member'>
                <div className='ui-member-details-header-family-unit-preloader__member__icon__preloader'></div>
                <div className='ui-member-details-header-family-unit-preloader__member__preloader'></div>
            </div>
            <div className='ui-member-details-header-family-unit-preloader__member'>
                <div className='ui-member-details-header-family-unit-preloader__member__icon__preloader'></div>
                <div className='ui-member-details-header-family-unit-preloader__member__preloader'></div>
            </div>
            <div className='ui-member-details-header-family-unit-preloader__member'>
                <div className='ui-member-details-header-family-unit-preloader__member__icon__preloader'></div>
                <div className='ui-member-details-header-family-unit-preloader__member__preloader'></div>
            </div>
        </div>
    );
};

// set display name
memberDetailsHeaderFamilyUnitPreloader.displayName = 'memberDetailsHeaderFamilyUnitPreloader';

// set default props
memberDetailsHeaderFamilyUnitPreloader.defaultProps = {};

// prop types
memberDetailsHeaderFamilyUnitPreloader.propTypes = {};

/*******************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { memberDetailsHeaderFamilyUnit as MemberDetailsHeaderFamilyUnit } from 'ui/views/widgets/memberDetailsHeaderFamilyUnit';
 *
 * // render
 * <MemberDetailsHeaderFamilyUnit propName='propValue'/>
 */
export const memberDetailsHeaderFamilyUnit = withNetworkState( memberDetailsHeaderFamilyUnitPreloader, memberDetailsHeaderFamilyUnitSuccess );
